import { Injectable } from '@angular/core';
import { LocalStorageService } from './local-storage.service';

export interface CopilotConversation {
  questions: any[];
  answers: any[];
}

export interface StoredConversationData {
  reportId: string;
  conversations: CopilotConversation;
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class CopilotStorageService {
  private readonly STORAGE_PREFIX = 'copilot_conversations_';

  constructor(private localStorageService: LocalStorageService) { }

  private encodeData(data: string): string {
    try {
      const bytes = new TextEncoder().encode(data);
      const binString = String.fromCodePoint(...bytes);
      return btoa(binString);
    } catch (error) {
      console.error('Error encoding data:', error);
      return data;
    }
  }

  private decodeData(encodedData: string): string {
    try {
      const binString = atob(encodedData);
      const bytes = Uint8Array.from(binString, (char) => char.codePointAt(0)!);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error('Error decoding data:', error);
      return encodedData;
    }
  }

  private getStorageKey(reportId: string): string {
    return `${this.STORAGE_PREFIX}${reportId}`;
  }

  saveConversations(reportId: string, conversations: CopilotConversation): void {
    if (!reportId) {
      console.warn('Cannot save conversations: reportId is required');
      return;
    }

    const key = this.getStorageKey(reportId);
    const data: StoredConversationData = {
      reportId: reportId,
      conversations: conversations,
      timestamp: new Date().toISOString()
    };

    const jsonString = JSON.stringify(data);
    const encodedData = this.encodeData(jsonString);

    this.localStorageService.setItem(key, encodedData);
  }

  loadConversations(reportId: string): CopilotConversation | null {
    if (!reportId) {
      return null;
    }

    const key = this.getStorageKey(reportId);
    const encodedData = this.localStorageService.getItem<string>(key);

    if (!encodedData) {
      return null;
    }

    try {
      const decodedString = this.decodeData(encodedData);
      const data: StoredConversationData = JSON.parse(decodedString);
      return data.conversations || null;
    } catch (error) {
      console.error('Error parsing conversation data:', error);
      return null;
    }
  }

  deleteConversations(reportId: string): void {
    if (!reportId) {
      return;
    }

    const key = this.getStorageKey(reportId);
    this.localStorageService.removeItem(key);
  }

  hasConversations(reportId: string): boolean {
    if (!reportId) {
      return false;
    }

    const key = this.getStorageKey(reportId);
    return this.localStorageService.hasItem(key);
  }

  clearAllConversations(): void {
    this.localStorageService.removeByPrefix(this.STORAGE_PREFIX);
  }
}
